'use client'

import React from 'react'
import { Flex } from '@mantine/core'
import { IconMenu2, IconSearch } from '@tabler/icons-react'
import { MobileSearchInput } from '@/components/MobileSearchInput'
import { useIsAnyDashboardHomePage } from '@/hooks/useAccountDashboardActive'
import { isAnySignupPath, isFocusedFlow } from '@/lib/namedRoutes'
import { useBreakpoint } from '@/hooks/useBreakpoints'
import { StoreAnalytics, trackStoreEvent } from '@nile/mantine-theme/analytics'
import { UserIcon } from '@/components/Icons/UserIcon'
import { BrowseButton } from './BrowseButton'
import { IconCircleButton } from './IconCircleButton'
import { NavLinkButton } from './NavLinkButton'
import { NavPillButton } from './NavPillButton'
import { TextUsButton } from './TextUsButton'
import { NavTheme } from './theme'

const trackHeaderClick = (name: string) => trackStoreEvent(StoreAnalytics.NAV_HEADER_CLICKED, { name })

interface HeaderActionsProps {
    pathname: string
    isLoggedIn: boolean
    hasFetchedUser: boolean
    isBlocked: boolean
    theme: NavTheme
    onOpenSearch: () => void
    onOpenMenu: () => void
}

export function HeaderActions(props: HeaderActionsProps) {
    if (isFocusedFlow(props.pathname)) {
        return <FocusedFlowActions theme={props.theme} onOpenMenu={props.onOpenMenu} />
    }
    return <StoreActions {...props} />
}

function FocusedFlowActions({ theme, onOpenMenu }: { theme: NavTheme; onOpenMenu: () => void }) {
    const { isSm, isMd, isLg, isXl } = useBreakpoint()
    if (!(isSm || isMd || isLg || isXl)) return null
    return (
        <Flex gap={16}>
            <TextUsButton theme={theme} iconOnly={isSm} />
            <MenuToggleButton theme={theme} onClick={onOpenMenu} />
        </Flex>
    )
}

function StoreActions({ pathname, isLoggedIn, hasFetchedUser, isBlocked, theme, onOpenSearch, onOpenMenu }: HeaderActionsProps) {
    const { isSm, isMd, isLg, isXl, isBelow990px } = useBreakpoint()
    const isDashboardHomePage = useIsAnyDashboardHomePage(pathname, isBlocked)

    if (isSm) {
        return (
            <Flex gap={12}>
                <SearchButton theme={theme} onClick={onOpenSearch} />
                <MenuToggleButton theme={theme} onClick={onOpenMenu} />
            </Flex>
        )
    }
    if (!(isMd || isLg || isXl)) return null

    const showSignIn = hasFetchedUser && !isLoggedIn && !isAnySignupPath(pathname) && !pathname.startsWith('/login')
    // The dashboard's own side nav already surfaces search, support, and sign-out,
    // so the header hamburger would just duplicate it on desktop.
    const showMenuButton = !isDashboardHomePage

    return (
        <Flex align="center" gap={16}>
            <Flex align="center" gap={16}>
                {!isBelow990px && (
                    <>
                        <NavLinkButton label="Medications" href="/store/medications" />
                        <NavLinkButton label="Labs" href="/store/labs" />
                        <NavLinkButton label="Specialty" href="/store/specialty-care" />
                        <BrowseButton />
                    </>
                )}
            </Flex>
            <Flex align="center" gap={12}>
                {showSignIn && (
                    <NavPillButton
                        href="/login"
                        label="Sign in"
                        onClick={() => trackHeaderClick('sign-in')}
                        data-testid="signin-button"
                    />
                )}
                {isLoggedIn && (
                    <NavPillButton
                        href="/account"
                        label="Account"
                        icon={<UserIcon color={theme.iconColor} />}
                        onClick={() => trackHeaderClick('account')}
                        data-testid="account-button"
                    />
                )}
                <SearchButton theme={theme} onClick={onOpenSearch} />
                {showMenuButton && <MenuToggleButton theme={theme} onClick={onOpenMenu} />}
            </Flex>
        </Flex>
    )
}

function SearchButton({ theme, onClick }: { theme: NavTheme; onClick: () => void }) {
    const { isSm } = useBreakpoint()
    const handleActivate = () => {
        onClick()
        trackStoreEvent(StoreAnalytics.SEARCH_START, { location: 'header' })
    }

    // On mobile, layer an invisible real <input> over the icon so the tap
    // produces a native focus event — that's what pops the iOS keyboard.
    if (isSm) {
        return (
            <MobileSearchInput
                onActivate={handleActivate}
                aria-label="Search"
                data-testid="search-for-care-button"
                style={{ width: 44, height: 44, border: theme.border, borderRadius: '999px' }}
            >
                <IconSearch size={20} color={theme.actionIconColor} />
            </MobileSearchInput>
        )
    }

    return (
        <IconCircleButton
            theme={theme}
            data-testid="search-for-care-button"
            aria-label="Search"
            onClick={handleActivate}
        >
            <IconSearch size={20} />
        </IconCircleButton>
    )
}

function MenuToggleButton({ theme, onClick }: { theme: NavTheme; onClick: () => void }) {
    return (
        <IconCircleButton
            theme={theme}
            data-testid="menu-button"
            aria-label="Open menu"
            onClick={() => {
                onClick()
                trackStoreEvent(StoreAnalytics.NAV_HAMBURGER_MENU, { action: 'open-menu' })
            }}
        >
            <IconMenu2 size={20} />
        </IconCircleButton>
    )
}
